(() => {
  function registerMakerFeatures() {
    const theme = window.KSTheme;

    if (!theme?.registerFeature) return;

    const {
      announce,
      collect,
      escapeHtml,
    } = theme;

  const facilityWeatherCache = new Map();

  function readMakerThankYouLocalStorage(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function writeMakerThankYouLocalStorage(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      // Storage can be unavailable in private browsing or restricted contexts.
    }
  }

  function formatFacilityTemperature(value, unit) {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
      return '';
    }

    return `${Math.round(numericValue)}°${unit === 'fahrenheit' ? 'F' : 'C'}`;
  }

  function getFacilityWeatherIconUrl(iconCode) {
    const code = String(iconCode || '').trim();

    if (!code) {
      return '';
    }

    return `https://openweathermap.org/img/wn/${encodeURIComponent(code)}@2x.png`;
  }

  async function fetchFacilityWeather(handle, unit) {
    const normalizedHandle = String(handle || '').trim().toLowerCase();
    const cacheKey = `${normalizedHandle}:${unit}`;

    if (facilityWeatherCache.has(cacheKey)) {
      return facilityWeatherCache.get(cacheKey);
    }

    const weatherPromise = (async () => {
      if (!normalizedHandle) {
        throw new Error('Missing facility handle.');
      }

      const params = new URLSearchParams({
        handle: normalizedHandle,
        range: 'daily',
        unit,
      });
      const response = await fetch(`/apps/maker-thanks/weather?${params.toString()}`, {
        headers: {
          Accept: 'application/json',
        },
      });
      const payload = await response.json().catch(() => null);

      if (!response.ok || !payload?.ok) {
        throw new Error(payload?.error?.message || 'Unable to load facility weather.');
      }

      return payload;
    })();

    facilityWeatherCache.set(cacheKey, weatherPromise);

    try {
      return await weatherPromise;
    } catch (error) {
      facilityWeatherCache.delete(cacheKey);
      throw error;
    }
  }

  function initFacilityHeroes(scope = document) {
    collect(scope, '[data-facility-hero]').forEach((section) => {
      if (section.dataset.facilityHeroInitialized === 'true') return;
      section.dataset.facilityHeroInitialized = 'true';

      const timezone = String(section.dataset.facilityTimezone || '').trim();
      const facilityHandle = String(section.dataset.facilityHandle || '').trim();
      const temperatureUnit = String(section.dataset.facilityTemperatureUnit || 'celsius').trim() || 'celsius';
      const timeNode = section.querySelector('[data-facility-time]');
      const weatherContainer = section.querySelector('[data-facility-weather]');
      const weatherTemp = section.querySelector('[data-facility-weather-temp]');
      const weatherIcon = section.querySelector('[data-facility-weather-icon]');
      const weatherSummary = section.querySelector('[data-facility-weather-summary]');
      const weatherFallbackIcon = String(section.dataset.facilityWeatherFallbackIcon || '').trim();
      if (timeNode && timezone) {
        const renderTime = () => {
          try {
            timeNode.textContent = new Intl.DateTimeFormat(undefined, {
              hour: 'numeric',
              minute: '2-digit',
              timeZone: timezone,
            }).format(new Date());
          } catch (error) {
            timeNode.textContent = 'Time unavailable';
          }
        };

        renderTime();
        window.setInterval(renderTime, 30000);
      }

      if (!weatherTemp || !weatherSummary) return;

      const revealWeather = () => {
        if (!weatherContainer) return;

        weatherContainer.setAttribute('aria-busy', 'false');
        weatherContainer.dataset.weatherReady = 'true';
      };

      const renderWeatherFallback = () => {
        weatherTemp.textContent = '—';
        weatherSummary.textContent = 'Weather unavailable';

        if (weatherIcon) {
          weatherIcon.hidden = true;
          weatherIcon.removeAttribute('src');
        }


        revealWeather();
      };

      if (!facilityHandle) {
        renderWeatherFallback();
        return;
      }

      fetchFacilityWeather(facilityHandle, temperatureUnit)
        .then((weather) => {
          const formattedTemperature = formatFacilityTemperature(weather.temperature, temperatureUnit);

          if (!formattedTemperature) {
            renderWeatherFallback();
            return;
          }

          weatherTemp.textContent = formattedTemperature;
          if (weatherIcon) {
            const iconUrl = getFacilityWeatherIconUrl(weather.icon);

            if (iconUrl) {
              weatherIcon.src = iconUrl;
              weatherIcon.hidden = false;
            } else if (weatherFallbackIcon) {
              weatherIcon.src = weatherFallbackIcon;
              weatherIcon.hidden = false;
            } else {
              weatherIcon.hidden = true;
              weatherIcon.removeAttribute('src');
            }
          }
          weatherSummary.textContent = weather.description || weather.summary || 'Conditions unavailable';


          revealWeather();
        })
        .catch(() => {
          renderWeatherFallback();
        });
    });
  }

  function initFacilityMakers(scope = document) {
    collect(scope, '[data-facility-makers]').forEach((section) => {
      if (section.dataset.facilityMakersInitialized === 'true') return;
      section.dataset.facilityMakersInitialized = 'true';

      const items = Array.from(section.querySelectorAll('[data-facility-makers-item]'));
      const searchInput = section.querySelector('[data-facility-makers-search]');
      const loadMoreButton = section.querySelector('[data-facility-makers-load-more]');
      const status = section.querySelector('[data-facility-makers-status]');
      const emptyState = section.querySelector('[data-facility-makers-empty]');
      const batchSize = Math.max(1, Number(section.dataset.loadCount || items.length || 1));
      const singularLabel = String(section.dataset.singularLabel || 'maker').trim() || 'maker';
      const pluralLabel = String(section.dataset.pluralLabel || 'makers').trim() || 'makers';
      let visibleCount = batchSize;

      function render() {
        const query = String(searchInput?.value || '').trim().toLowerCase();
        const matchingItems = items.filter((item) => (
          String(item.dataset.searchText || '').toLowerCase().includes(query)
        ));

        items.forEach((item) => {
          const matchIndex = matchingItems.indexOf(item);
          item.hidden = matchIndex < 0 || matchIndex >= visibleCount;
        });

        if (status) {
          const shownCount = Math.min(visibleCount, matchingItems.length);
          const label = matchingItems.length === 1 ? singularLabel : pluralLabel;
          status.textContent = `Showing ${shownCount} of ${matchingItems.length} ${label}`;
        }

        if (loadMoreButton) {
          loadMoreButton.hidden = visibleCount >= matchingItems.length;
          loadMoreButton.disabled = false;
        }

        if (emptyState) {
          emptyState.hidden = matchingItems.length !== 0;
        }
      }

      if (loadMoreButton) {
        loadMoreButton.addEventListener('click', () => {
          visibleCount += batchSize;
          render();
        });
      }

      if (searchInput) {
        searchInput.addEventListener('input', () => {
          visibleCount = batchSize;
          render();
        });
      }

      render();
    });
  }

  function normalizeMakerSearchText(value) {
    return String(value || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/đ/g, 'd')
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  function initFacilityMakerSearch(scope = document) {
    collect(scope, '[data-facility-maker-search]').forEach((section) => {
      if (section.dataset.facilityMakerSearchInitialized === 'true') return;
      section.dataset.facilityMakerSearchInitialized = 'true';

      const input = section.querySelector('[data-facility-maker-search-input]');
      const items = Array.from(section.querySelectorAll('[data-facility-maker-search-item]'));
      const status = section.querySelector('[data-facility-maker-search-status]');
      const emptyState = section.querySelector('[data-facility-maker-search-empty]');
      const initialLabel = String(section.dataset.initialLabel || 'Start typing to search makers.').trim();
      const resultLimit = Math.max(1, Number(section.dataset.resultLimit || items.length || 1));
      const prepopulateRandom = section.dataset.prepopulateRandom === 'true';
      const facilityLookup = section.dataset.facilityLookup === 'true';
      const singularLabel = String(section.dataset.singularLabel || 'maker').trim() || 'maker';
      const pluralLabel = String(section.dataset.pluralLabel || 'makers').trim() || 'makers';
      const randomizedItems = items.filter((item) => item.dataset.hasImage !== 'false');

      for (let index = randomizedItems.length - 1; index > 0; index -= 1) {
        const randomIndex = Math.floor(Math.random() * (index + 1));
        [randomizedItems[index], randomizedItems[randomIndex]] = [randomizedItems[randomIndex], randomizedItems[index]];
      }

      const initialItems = new Set(randomizedItems.slice(0, resultLimit));
      let nextPage = section.querySelector('[data-maker-next-page]')?.getAttribute('href');
      let loading = false;
      let loadFailed = false;
      const visitedPages = new Set();
      const makerKeys = new Set(items.map((item) => item.dataset.makerKey));
      const results = section.querySelector('[data-facility-maker-search-results]');
      const loadPortrait = (item) => {
        const image = item.querySelector('[data-maker-image]');
        if (item.hidden || !image || image.dataset.makerImageStarted) return;
        image.dataset.makerImageStarted = 'true';
        image.sizes = `${Math.max(1, Math.ceil(image.getBoundingClientRect().width))}px`;
        image.loading = 'eager';
        const reveal = async () => {
          try { await image.decode(); } catch (_) { /* Failed images must not block the card. */ }
          image.classList.add('is-maker-image-ready');
        };
        image.addEventListener('load', reveal, { once: true });
        image.addEventListener('error', reveal, { once: true });
        if (image.dataset.makerSrcset) image.srcset = image.dataset.makerSrcset;
        if (image.dataset.makerSrc) image.src = image.dataset.makerSrc;
        image.removeAttribute('data-maker-srcset');
        image.removeAttribute('data-maker-src');
        if (image.complete) reveal();
      };
      const portraitObserver = typeof IntersectionObserver === 'function'
        ? new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting || entry.target.hidden) return;
            loadPortrait(entry.target);
            portraitObserver.unobserve(entry.target);
          });
        }, { rootMargin: '300px 0px' }) : null;
      const cleanupPortraits = (event) => {
        if (!event.target.contains(section)) return;
        portraitObserver?.disconnect();
        document.removeEventListener('shopify:section:unload', cleanupPortraits);
      };
      document.addEventListener('shopify:section:unload', cleanupPortraits);

      async function loadRemainingMakers() {
        if (loading || !nextPage) return;
        loading = true;
        loadFailed = false;
        section.setAttribute('aria-busy', 'true');
        update();
        try {
          while (nextPage && section.isConnected) {
            const url = new URL(nextPage, window.location.href);
            if (url.origin !== window.location.origin || visitedPages.has(url.href)) throw new Error('Invalid maker pagination');
            url.searchParams.set('section_id', section.dataset.makerSectionId);
            const response = await fetch(url.href, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
            if (!response.ok) throw new Error('Unable to load makers');
            const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
            const incoming = Array.from(parsed.querySelectorAll('[data-facility-maker-search]')).find((candidate) => candidate.dataset.makerSectionId === section.dataset.makerSectionId);
            if (!incoming) throw new Error('Missing maker results');
            if (!section.isConnected) return;
            visitedPages.add(new URL(nextPage, window.location.href).href);
            incoming.querySelectorAll('[data-facility-maker-search-item]').forEach((item) => {
              if (!item.dataset.makerKey || makerKeys.has(item.dataset.makerKey)) return;
              makerKeys.add(item.dataset.makerKey);
              item.hidden = true;
              results.append(item);
              items.push(item);
              if (facilityLookup && prepopulateRandom && initialItems.size < resultLimit && item.dataset.hasImage !== 'false') {
                initialItems.add(item);
              }
            });
            nextPage = incoming.querySelector('[data-maker-next-page]')?.getAttribute('href');
            update();
            // Initial facility browsing needs only enough portraits to fill the grid.
            // A typed search continues through every remaining page.
            if (!input?.value.trim() && initialItems.size >= resultLimit) break;
          }
        } catch (error) {
          loadFailed = true;
        } finally {
          loading = false;
          section.removeAttribute('aria-busy');
          update();
        }
      }

      function update() {
        const query = normalizeMakerSearchText(input?.value);
        let matchCount = 0;
        let visibleCount = 0;

        items.forEach((item) => {
          const haystack = normalizeMakerSearchText(item.dataset.searchText);
          const isMatch = query !== '' ? haystack.includes(query) : prepopulateRandom && initialItems.has(item);

          if (isMatch) {
            matchCount += 1;
          }

          const shouldShow = isMatch && visibleCount < resultLimit;
          item.hidden = !shouldShow;

          if (shouldShow) {
            if (portraitObserver) portraitObserver.observe(item);
            else loadPortrait(item);
          } else {
            portraitObserver?.unobserve(item);
          }

          if (shouldShow) visibleCount += 1;
        });

        if (status) {
          if (loading && query === '' && facilityLookup && initialItems.size === 0) {
            status.textContent = 'Loading makers…';
          } else if (loadFailed && query === '' && facilityLookup && initialItems.size === 0) {
            status.textContent = 'Makers could not load. Type a name to retry.';
          } else if (loading && query !== '') {
            status.textContent = `Searching all makers… ${items.length} loaded.`;
          } else if (loadFailed && query !== '') {
            status.textContent = 'Some makers could not load. Edit your search to retry.';
          } else if (query === '') {
            status.textContent = initialLabel;
          } else {
            const resultLabel = matchCount === 1 ? singularLabel : pluralLabel;
            status.textContent = matchCount > visibleCount
              ? `Showing ${visibleCount} of ${matchCount} matching ${resultLabel}`
              : `${visibleCount} matching ${resultLabel}`;
          }
        }

        if (emptyState) {
          const showInitialEmpty = facilityLookup && prepopulateRandom && initialItems.size === 0;
          emptyState.hidden = (query === '' && !showInitialEmpty) || matchCount !== 0 || loading || loadFailed || Boolean(nextPage);
        }
      }

      if (input) {
        input.addEventListener('input', () => {
          update();
          if (input.value.trim()) loadRemainingMakers();
        });
      }

      update();
      if (input?.value.trim() || (facilityLookup && prepopulateRandom && initialItems.size < resultLimit)) loadRemainingMakers();
    });
  }

  async function loadMakerThankYouNotes({ makerId, limit = 6, cursor } = {}) {
    const params = new URLSearchParams({
      limit: String(limit),
    });

    if (makerId) {
      params.set('makerId', makerId);
    }


    if (cursor) {
      params.set('cursor', cursor);
    }

    const response = await fetch(`/apps/maker-thanks/notes?${params.toString()}`, {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    return parseMakerThankYouJsonResponse(response, {
      invalidResponseMessage: 'Unable to load notes right now.',
      passwordProtectedMessage: 'Notes are unavailable in password-protected previews. Open the storefront after entering the password.',
    });
  }

  async function submitMakerThankYouNote(payload) {
    const response = await fetch('/apps/maker-thanks/submit', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    });

    return parseMakerThankYouJsonResponse(response, {
      invalidResponseMessage: 'Unable to send your note right now.',
      passwordProtectedMessage: 'Note submission is unavailable in password-protected previews. Open the storefront after entering the password.',
    });
  }

  const KLAVIYO_CLIENT_API_REVISION = '2026-04-15';
  const MAKER_THANKYOU_KLAVIYO_SOURCE = 'thank_you_note_form';

  function isValidKlaviyoEmailAddress(value) {
    const normalizedValue = String(value || '').trim();

    if (normalizedValue === '' || normalizedValue.length > 254) return false;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedValue)) return false;
    if (/\.\./.test(normalizedValue)) return false;

    return true;
  }

  function getMakerThankYouFirstName(value) {
    const normalizedValue = String(value || '').trim().replace(/\s+/g, ' ');

    if (normalizedValue === '') return '';

    return normalizedValue.split(' ')[0].slice(0, 80);
  }

  function compactMakerThankYouProperties(properties = {}) {
    return Object.entries(properties).reduce((result, [key, value]) => {
      if (value === null || value === undefined) {
        return result;
      }

      if (typeof value === 'string' && value.trim() === '') {
        return result;
      }

      result[key] = value;
      return result;
    }, {});
  }

  function buildMakerThankYouKlaviyoProfileProperties({
    makerId = '',
    makerName = '',
    marketingConsent = false,
    submittedAt = '',
  } = {}) {
    return compactMakerThankYouProperties({
      maker_id: String(makerId || '').trim(),
      maker_name: String(makerName || '').trim(),
      marketing_consent: Boolean(marketingConsent),
      submitted_at: String(submittedAt || '').trim(),
      source: MAKER_THANKYOU_KLAVIYO_SOURCE,
    });
  }


  async function readMakerThankYouKlaviyoError(response) {
    const fallbackMessage = `HTTP ${Number(response?.status || 0) || 0}`;
    const contentType = String(response?.headers?.get('content-type') || '').toLowerCase();

    try {
      if (contentType.includes('json')) {
        const data = await response.json();
        const detail = data?.errors?.[0]?.detail || data?.errors?.[0]?.title;

        return detail ? String(detail).trim() : fallbackMessage;
      }

      const text = await response.text();

      return text ? text.trim() : fallbackMessage;
    } catch (error) {
      return fallbackMessage;
    }
  }

  async function postMakerThankYouKlaviyoClientRequest(path, {
    companyId = '',
    body = {},
  } = {}) {
    const normalizedCompanyId = String(companyId || '').trim();

    if (!normalizedCompanyId) {
      throw new Error('Missing Klaviyo site ID.');
    }

    const requestUrl = new URL(path, 'https://a.klaviyo.com');
    requestUrl.searchParams.set('company_id', normalizedCompanyId);

    const response = await fetch(requestUrl.toString(), {
      method: 'POST',
      headers: {
        Accept: 'application/vnd.api+json',
        'Content-Type': 'application/vnd.api+json',
        revision: KLAVIYO_CLIENT_API_REVISION,
      },
      body: JSON.stringify(body),
      credentials: 'omit',
      keepalive: true,
    });

    if (!response.ok) {
      throw new Error(await readMakerThankYouKlaviyoError(response));
    }
  }

  async function createMakerThankYouKlaviyoProfile({
    companyId = '',
    email = '',
    firstName = '',
    makerId = '',
    makerName = '',
    marketingConsent = false,
    submittedAt = '',
  } = {}) {
    const profileProperties = buildMakerThankYouKlaviyoProfileProperties({
      makerId,
      makerName,
      marketingConsent,
      submittedAt,
    });

    await postMakerThankYouKlaviyoClientRequest('/client/profiles', {
      companyId,
      body: {
        data: {
          type: 'profile',
          attributes: compactMakerThankYouProperties({
            email: String(email || '').trim(),
            first_name: String(firstName || '').trim(),
            properties: profileProperties,
          }),
        },
      },
    });
  }


  async function createMakerThankYouKlaviyoSubscription({
    companyId = '',
    listId = '',
    email = '',
    firstName = '',
    makerId = '',
    makerName = '',
    marketingConsent = false,
    submittedAt = '',
  } = {}) {
    const normalizedListId = String(listId || '').trim();

    if (!normalizedListId) {
      throw new Error('Missing Klaviyo list ID.');
    }

    const profileProperties = buildMakerThankYouKlaviyoProfileProperties({
      makerId,
      makerName,
      marketingConsent,
      submittedAt,
    });

    await postMakerThankYouKlaviyoClientRequest('/client/subscriptions', {
      companyId,
      body: {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: MAKER_THANKYOU_KLAVIYO_SOURCE,
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: String(email || '').trim(),
                  first_name: String(firstName || '').trim(),
                  properties: profileProperties,
                  subscriptions: {
                    email: {
                      marketing: {
                        consent: 'SUBSCRIBED',
                      },
                    },
                  },
                },
              },
            },
          },
          relationships: {
            list: {
              data: {
                type: 'list',
                id: normalizedListId,
              },
            },
          },
        },
      },
    });
  }

  async function syncMakerThankYouWithKlaviyo({
    companyId = '',
    listId = '',
    email = '',
    authorName = '',
    makerId = '',
    makerName = '',
    marketingConsent = false,
    submittedAt = '',
  } = {}) {
    const normalizedCompanyId = String(companyId || '').trim();
    const normalizedEmail = String(email || '').trim();

    if (!normalizedCompanyId || !isValidKlaviyoEmailAddress(normalizedEmail)) {
      return;
    }

    const klaviyoPayload = {
      companyId: normalizedCompanyId,
      listId: String(listId || '').trim(),
      email: normalizedEmail,
      firstName: getMakerThankYouFirstName(authorName),
      makerId: String(makerId || '').trim(),
      makerName: String(makerName || '').trim(),
      marketingConsent: Boolean(marketingConsent),
      submittedAt: String(submittedAt || '').trim() || new Date().toISOString(),
    };

    try {
      await createMakerThankYouKlaviyoProfile(klaviyoPayload);
    } catch (error) {
      console.warn('Maker thank-you Klaviyo profile sync failed.', error);
    }


    if (!klaviyoPayload.marketingConsent) {
      return;
    }

    if (!klaviyoPayload.listId) {
      console.warn('Maker thank-you Klaviyo list ID is not configured. Skipping marketing subscription.');
      return;
    }

    try {
      await createMakerThankYouKlaviyoSubscription(klaviyoPayload);
    } catch (error) {
      console.warn('Maker thank-you Klaviyo subscription sync failed.', error);
    }
  }

  async function parseMakerThankYouJsonResponse(response, {
    invalidResponseMessage = 'Unable to complete this request right now.',
    passwordProtectedMessage = 'This feature is unavailable from password-protected previews.',
  } = {}) {
    const responseUrl = String(response?.url || '');
    const contentType = String(response?.headers?.get('content-type') || '').toLowerCase();
    const isJsonLikeResponse = (
      contentType.includes('json')
      || contentType.includes('javascript')
      || contentType.includes('ecmascript')
    );

    if (response.redirected && /\/password(?:[?#]|$)/.test(responseUrl)) {
      throw {
        code: 'password_protected_preview',
        error: passwordProtectedMessage,
      };
    }

    if (!isJsonLikeResponse) {
      let responseText = '';

      try {
        responseText = await response.text();
      } catch (error) {
        responseText = '';
      }

      if (
        /\/password(?:[?#]|$)/.test(responseUrl)
        || /<title>[^<]*password/i.test(responseText)
      ) {
        throw {
          code: 'password_protected_preview',
          error: passwordProtectedMessage,
        };
      }

      try {
        const data = JSON.parse(responseText);

        if (!response.ok) {
          throw data;
        }

        return data;
      } catch (error) {
        if (error && typeof error === 'object' && !Array.isArray(error) && ('error' in error || 'errors' in error || 'code' in error)) {
          throw error;
        }

        throw {
          code: 'invalid_response',
          error: invalidResponseMessage,
        };
      }
    }

    let data;

    try {
      data = await response.json();
    } catch (error) {
      throw {
        code: 'invalid_json',
        error: invalidResponseMessage,
      };
    }

    if (!response.ok) {
      throw data;
    }

    return data;
  }

  function formatMakerThankYouDate(value) {
    try {
      return new Date(value).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch (error) {
      return '';
    }
  }

  function countUrls(value) {
    return (String(value || '').match(/(?:https?:\/\/|www\.)/gi) || []).length;
  }

  function validateMakerThankYouSubmission(payload, { cooldownMs = 60000, elapsedMs = 0, makerId = '' } = {}) {
    const fieldErrors = {};
    const authorName = String(payload?.authorName || '').trim();
    const authorEmail = String(payload?.authorEmail || '').trim();
    const message = String(payload?.message || '').trim();
    const company = String(payload?.company || '').trim();
    if (company !== '') {
      fieldErrors.message = 'Unable to send your note right now.';
      return fieldErrors;
    }

    if (elapsedMs > 0 && elapsedMs < 2500) {
      fieldErrors.message = 'Please take a moment before sending your note.';
    }

    if (authorName.length > 0 && authorName.length < 2) {
      fieldErrors.authorName = 'Enter your first name.';
    }

    if (message.length > 0 && message.length < 6) {
      fieldErrors.message = 'Write a slightly longer note before sending.';
    }

    if (/<[^>]+>/.test(authorName)) {
      fieldErrors.authorName = 'Enter your name without markup.';
    }

    if (/<[^>]+>/.test(message)) {
      fieldErrors.message = 'Remove HTML or code from your note and try again.';
    }

    if (/https?:\/\/|www\./i.test(authorName)) {
      fieldErrors.authorName = 'Enter your name only.';
    }

    if (/https?:\/\/|www\./i.test(authorEmail)) {
      fieldErrors.authorEmail = 'Enter a valid email address.';
    }

    if (countUrls(message) > 2) {
      fieldErrors.message = 'Please remove extra links from your note.';
    }

    if (/(.)\1{11,}/.test(message)) {
      fieldErrors.message = 'Please revise your note and try again.';
    }

    if (makerId) {
      const storageKey = `ks-maker-thankyou-last-submit:${makerId}`;
      const lastSubmittedAt = Number(readMakerThankYouLocalStorage(storageKey) || 0);

      if (lastSubmittedAt > 0 && Date.now() - lastSubmittedAt < cooldownMs) {
        fieldErrors.message = 'A note was already sent to this maker from this browser. Please wait before sending another.';
      }
    }

    return fieldErrors;
  }

  function renderMakerThankYouNote(note) {
    return `
      <article class="maker-thankyou__note">
        <div class="maker-thankyou__note-meta">
          <h3 class="maker-thankyou__note-author">${escapeHtml(note.authorName || '')}</h3>
          <p class="maker-thankyou__note-date">${escapeHtml(formatMakerThankYouDate(note.createdAt))}</p>
        </div>
        <div class="maker-thankyou__note-message">${escapeHtml(note.message || '').replace(/\n/g, '<br>')}</div>
      </article>
    `;
  }

  function renderMakerThankYouNotes(container, notes) {
    container.innerHTML = notes.map((note) => renderMakerThankYouNote(note)).join('');
  }

  function layoutMakerThankYouNoteGrid(container) {
    if (!container?.classList.contains('maker-thankyou-notes__grid')) return;

    const notes = Array.from(container.querySelectorAll('.maker-thankyou__note'));
    if (container.classList.contains('maker-thankyou-notes__grid--mobile-scroll')) {
      container.tabIndex = container.scrollWidth > container.clientWidth + 4 ? 0 : -1;
    }

    notes.forEach((note) => {
      note.style.gridRowEnd = 'auto';
    });

    if (
      container.classList.contains('maker-thankyou-notes__grid--mobile-scroll')
      && window.matchMedia('(max-width: 767px)').matches
    ) {
      return;
    }

    const rowHeight = Number.parseFloat(window.getComputedStyle(container).gridAutoRows);

    if (!Number.isFinite(rowHeight) || rowHeight <= 0) return;

    notes.forEach((note) => {
      const noteStyles = window.getComputedStyle(note);
      const noteHeight = note.getBoundingClientRect().height;
      const marginBottom = Number.parseFloat(noteStyles.marginBottom) || 0;
      const rowSpan = Math.max(1, Math.ceil((noteHeight + marginBottom) / rowHeight));

      note.style.gridRowEnd = `span ${rowSpan}`;
    });
  }

  function initMakerThankYou(scope = document) {
    collect(scope, '[data-maker-thankyou]').forEach((section) => {
      if (section.dataset.makerThankyouInitialized === 'true') return;
      section.dataset.makerThankyouInitialized = 'true';

      const makerId = String(section.dataset.makerId || '').trim();
      const makerName = String(section.dataset.makerName || '').trim();
      const notesLimitDesktop = Math.max(1, Number(section.dataset.notesLimit || 6));
      const notesLimitMobile = Math.max(1, Number(section.dataset.notesLimitMobile || notesLimitDesktop));
      const notesMobileMedia = window.matchMedia('(max-width: 767px)');
      const loadMoreLabel = section.dataset.loadMoreLabel || 'Load more';
      const noMoreLabel = section.dataset.noMoreLabel || 'No more notes';
      const klaviyoSiteId = String(section.dataset.klaviyoSiteId || '').trim();
      const klaviyoListId = String(section.dataset.klaviyoListId || '').trim();
      const submissionCooldownHours = Math.max(1, Number(section.dataset.submissionCooldownHours || 24));
      const submissionCooldownMs = submissionCooldownHours * 60 * 60 * 1000;
      const notesContainer = section.querySelector('[data-maker-thankyou-notes]');
      const notesProgress = section.querySelector('[data-maker-thankyou-notes-progress]');
      const notesStatus = section.querySelector('[data-maker-thankyou-notes-status]');
      const emptyState = section.querySelector('[data-maker-thankyou-empty]');
      const notesError = section.querySelector('[data-maker-thankyou-notes-error]');
      const loadMoreButton = section.querySelector('[data-maker-thankyou-load-more]');
      const form = section.querySelector('[data-maker-thankyou-form]');
      const formStatus = section.querySelector('[data-maker-thankyou-form-status]');
      const successState = section.querySelector('[data-maker-thankyou-success]');
      const successMessage = section.querySelector('[data-maker-thankyou-success-message]');
      const submitButton = section.querySelector('[data-maker-thankyou-submit]');
      const submitButtonText = section.querySelector('[data-maker-thankyou-submit-text]');
      const marketingConsentInput = section.querySelector('[data-maker-thankyou-marketing-consent]');
      const marketingConsentContainer = section.querySelector('[data-maker-thankyou-consent]');
      let formStartedAt = Date.now();
      let nextCursor = null;
      let loadingMore = false;
      let notesLayoutFrame = null;
      let notesProgressFrame = null;

      const getNotesLimit = () => (
        notesMobileMedia.matches ? notesLimitMobile : notesLimitDesktop
      );


      function clearFieldErrors() {
        collect(section, '[data-field-error]').forEach((node) => {
          node.textContent = '';
        });
      }

      function clearFieldError(field) {
        const errorNode = section.querySelector(`[data-field-error="${field}"]`);

        if (errorNode) {
          errorNode.textContent = '';
        }
      }

      function setFieldErrors(fieldErrors = {}) {
        Object.entries(fieldErrors).forEach(([field, message]) => {
          const errorNode = section.querySelector(`[data-field-error="${field}"]`);
          if (errorNode) {
            errorNode.textContent = message;
          }
        });
      }

      function updateMarketingConsentState() {
        marketingConsentContainer?.classList.toggle('is-selected', Boolean(marketingConsentInput?.checked));
      }

      function updateLoadMoreButton(pageInfo) {
        if (!loadMoreButton) return;

        nextCursor = pageInfo?.nextCursor || null;
        const hasNextPage = Boolean(pageInfo?.hasNextPage && nextCursor);

        loadMoreButton.hidden = !hasNextPage;
        loadMoreButton.disabled = false;
        loadMoreButton.textContent = hasNextPage ? loadMoreLabel : noMoreLabel;
      }

      function scheduleNotesLayout() {
        if (!notesContainer || notesLayoutFrame) return;

        notesLayoutFrame = window.requestAnimationFrame(() => {
          layoutMakerThankYouNoteGrid(notesContainer);
          updateNotesProgress();
          notesLayoutFrame = null;
        });
      }

      function updateNotesProgress() {
        if (!notesContainer || !notesProgress) return;

        const notes = Array.from(notesContainer.querySelectorAll('.maker-thankyou__note'));
        const maxScrollLeft = Math.max(0, notesContainer.scrollWidth - notesContainer.clientWidth);
        const position = maxScrollLeft > 0 && notes.length > 1
          ? Math.max(0, Math.min(notes.length - 1, (notesContainer.scrollLeft / maxScrollLeft) * (notes.length - 1)))
          : 0;

        notesProgress.hidden = notes.length < 2 || maxScrollLeft <= 4;
        notesProgress.style.setProperty('--maker-notes-progress-count', String(Math.max(1, notes.length)));
        notesProgress.style.setProperty('--maker-notes-progress-index', String(position));
        notesProgress.setAttribute('aria-valuemax', String(Math.max(1, notes.length)));
        notesProgress.setAttribute('aria-valuenow', String(Math.round(position) + 1));
      }

      if (notesContainer) {
        notesContainer.addEventListener('load', scheduleNotesLayout, true);
        notesContainer.addEventListener('scroll', () => {
          if (notesProgressFrame) return;

          notesProgressFrame = window.requestAnimationFrame(() => {
            updateNotesProgress();
            notesProgressFrame = null;
          });
        }, { passive: true });

        if (typeof ResizeObserver === 'function') {
          let previousNotesWidth = 0;
          const notesResizeObserver = new ResizeObserver(([entry]) => {
            const nextWidth = entry?.contentRect?.width || 0;

            if (Math.abs(nextWidth - previousNotesWidth) < 0.5) return;

            previousNotesWidth = nextWidth;
            scheduleNotesLayout();
          });

          notesResizeObserver.observe(notesContainer);
        }

        document.fonts?.ready.then(scheduleNotesLayout).catch(() => {});
      }

      async function fetchAndRenderNotes({ cursor, append = false } = {}) {
        if (!notesContainer || !makerId) return;

        if (!append) {
          notesContainer.innerHTML = '';
        }

        if (notesStatus) {
          notesStatus.textContent = cursor ? 'Loading more notes...' : 'Loading notes...';
        }

        if (notesError) notesError.hidden = true;
        if (emptyState) emptyState.hidden = true;

        try {
          const data = await loadMakerThankYouNotes({
            cursor,
            limit: getNotesLimit(),
            makerId,
          });
          const notes = Array.isArray(data?.notes) ? data.notes : [];

          if (append && notes.length > 0) {
            notesContainer.insertAdjacentHTML('beforeend', notes.map((note) => renderMakerThankYouNote(note)).join(''));
          } else if (notes.length > 0) {
            renderMakerThankYouNotes(notesContainer, notes);
          } else if (!append) {
            notesContainer.innerHTML = '';
          }

          // Hide only the notes section, never the separate submission form.
          // An empty load-more response must not discard existing notes.
          const notesSection = section.closest('.maker-thankyou-notes');
          if (notesSection) {
            const wrapper = notesSection.closest('.shopify-section') || notesSection;
            wrapper.hidden = !notesContainer.querySelector('.maker-thankyou__note');
          }

          layoutMakerThankYouNoteGrid(notesContainer);
          updateNotesProgress();

          if (notesStatus) {
            notesStatus.textContent = '';
          }

          if (notes.length === 0 && !append && emptyState) {
            emptyState.hidden = false;
          }

          updateLoadMoreButton(data?.pageInfo);
        } catch (error) {
          if (notesStatus) {
            notesStatus.textContent = '';
          }
          if (notesError) {
            notesError.hidden = false;
          }
          if (!append) {
            notesContainer.innerHTML = '';
          }

          if (loadMoreButton) {
            loadMoreButton.disabled = false;
          }
        }
      }

      if (loadMoreButton) {
        loadMoreButton.addEventListener('click', async () => {
          if (!nextCursor || loadingMore) return;

          loadingMore = true;
          loadMoreButton.disabled = true;
          await fetchAndRenderNotes({ cursor: nextCursor, append: true });
          loadingMore = false;
        });
      }

      if (notesContainer) {
        notesMobileMedia.addEventListener('change', () => {
          nextCursor = null;
          notesContainer.scrollLeft = 0;
          fetchAndRenderNotes();
        });
      }

      if (makerId && notesContainer) {
        fetchAndRenderNotes();
      }

      if (!form || !makerId) return;

      const submissionStorageKey = `ks-maker-thankyou-last-submit:${makerId}`;

      function getSubmissionCooldownRemaining() {
        const lastSubmittedAt = Number(readMakerThankYouLocalStorage(submissionStorageKey) || 0);

        if (lastSubmittedAt <= 0) return 0;

        return Math.max(0, submissionCooldownMs - (Date.now() - lastSubmittedAt));
      }

      function formatSubmissionCooldown(remainingMs) {
        const remainingHours = Math.ceil(remainingMs / (60 * 60 * 1000));

        if (remainingHours > 1) {
          return `${remainingHours} hours`;
        }

        const remainingMinutes = Math.max(1, Math.ceil(remainingMs / (60 * 1000)));
        return `${remainingMinutes} ${remainingMinutes === 1 ? 'minute' : 'minutes'}`;
      }

      function showSubmissionCooldownState(remainingMs) {
        form.hidden = true;

        if (successState) successState.hidden = false;
        if (successMessage) {
          successMessage.textContent = `A note for this maker was already sent from this browser. You can send another in about ${formatSubmissionCooldown(remainingMs)}.`;
        }
      }

      const initialCooldownRemaining = getSubmissionCooldownRemaining();

      if (initialCooldownRemaining > 0) {
        showSubmissionCooldownState(initialCooldownRemaining);
        return;
      }

      if (marketingConsentInput) {
        marketingConsentInput.addEventListener('change', updateMarketingConsentState);
      }

      collect(form, 'input[name="authorName"], input[name="authorEmail"], textarea[name="message"]').forEach((field) => {
        field.addEventListener('input', () => {
          clearFieldError(field.name);
        });
      });

      updateMarketingConsentState();

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        clearFieldErrors();
        if (formStatus) formStatus.textContent = '';

        const formData = new FormData(form);
        const company = String(formData.get('company') || '');
        const marketingConsent = String(formData.get('marketingConsent') || '').trim() === 'true';
        const payload = {
          makerId: String(formData.get('makerId') || '').trim(),
          authorName: String(formData.get('authorName') || '').trim(),
          authorEmail: String(formData.get('authorEmail') || '').trim(),
          message: String(formData.get('message') || '').trim(),
          marketingConsent,
          company,
        };
        if (company.trim() !== '') {
          return;
        }


        const clientFieldErrors = validateMakerThankYouSubmission(
          payload,
          {
            cooldownMs: submissionCooldownMs,
            elapsedMs: Date.now() - formStartedAt,
            makerId,
          }
        );

        if (Object.keys(clientFieldErrors).length > 0) {
          setFieldErrors(clientFieldErrors);
          return;
        }


        if (submitButton) submitButton.disabled = true;
        if (submitButtonText) submitButtonText.textContent = 'Sending...';

        try {
          const response = await submitMakerThankYouNote(payload);
          const submittedAt = new Date().toISOString();
          const klaviyoPayload = {
            companyId: klaviyoSiteId,
            listId: klaviyoListId,
            email: payload.authorEmail,
            authorName: payload.authorName,
            makerId,
            makerName,
            marketingConsent,
            submittedAt,
          };

          writeMakerThankYouLocalStorage(submissionStorageKey, String(Date.now()));
          formStartedAt = Date.now();
          announce(response?.message || 'Thanks for sharing a note.');
          syncMakerThankYouWithKlaviyo(klaviyoPayload);

          if (formStatus) {
            formStatus.textContent = response?.message || 'Thanks for sharing a note.';
          }
          form.reset();
          updateMarketingConsentState();
          form.hidden = true;
          if (successState) successState.hidden = false;
        } catch (error) {
          if (error?.fieldErrors) {
            setFieldErrors(error.fieldErrors);
          }

          if (formStatus) {
            formStatus.textContent = error?.error || 'Unable to send your note right now.';
          }
        } finally {
          if (submitButton) submitButton.disabled = false;
          if (submitButtonText) submitButtonText.textContent = submitButton.dataset.originalLabel || 'Submit';
        }
      });

      if (submitButton && !submitButton.dataset.originalLabel) {
        submitButton.dataset.originalLabel = submitButtonText ? submitButtonText.textContent : 'Submit';
      }
    });
  }

    theme.registerFeature('maker-features', (scope = document) => {
      initFacilityHeroes(scope);
      initFacilityMakers(scope);
      initFacilityMakerSearch(scope);
      initMakerThankYou(scope);
    });
  }

  if (window.KSTheme?.registerFeature) {
    registerMakerFeatures();
  } else {
    document.addEventListener('ks:theme:ready', registerMakerFeatures, { once: true });
  }
})();
